import type { PersonSource } from "./model";

export interface Resolved {
  target_key?: string;
  display_name: string;
  source: PersonSource;
}

// Map known hosts to a short platform slug. Anything else falls back to the
// bare hostname, so the identity pointer is still stable.
const PLATFORMS: Record<string, string> = {
  "linkedin.com": "linkedin",
  "www.linkedin.com": "linkedin",
  "twitter.com": "x",
  "x.com": "x",
  "www.x.com": "x",
  "github.com": "github",
  "www.github.com": "github",
};

// Resolve a pasted profile URL to an identity POINTER only: "platform:handle".
// We never fetch the page or store any scraped content. If the input is not a
// URL it is treated as a plain display name (manual entry).
export function resolveProfile(input: string): Resolved {
  const text = input.trim();
  if (!text) return { display_name: "", source: "manual" };

  let url: URL | undefined;
  try {
    url = new URL(text.includes("://") ? text : `https://${text}`);
  } catch {
    url = undefined;
  }

  // Only treat it as a link when it actually looks like one, otherwise a name
  // with a dot ("J. Smith") would be misread as a host.
  const looksLikeLink = /^(https?:\/\/|[\w-]+\.[\w.-]+\/)/i.test(text);
  if (!url || !looksLikeLink) {
    return { display_name: text, source: "manual" };
  }

  const host = url.hostname.toLowerCase();
  const platform = PLATFORMS[host] ?? host.replace(/^www\./, "");
  const segments = url.pathname.split("/").filter(Boolean);
  // LinkedIn nests the handle under /in/.
  const handle =
    platform === "linkedin" && segments[0] === "in"
      ? segments[1]
      : segments[0];

  if (!handle) {
    return { target_key: platform, display_name: platform, source: "url" };
  }
  return {
    target_key: `${platform}:${handle}`,
    display_name: decodeURIComponent(handle),
    source: "url",
  };
}

// Reconstruct a clickable profile URL from a stored "platform:handle" pointer.
// Returns null for local/import keys that carry no real profile.
export function profileUrl(targetKey: string): string | null {
  const i = targetKey.indexOf(":");
  if (i < 0) return null;
  const platform = targetKey.slice(0, i);
  const handle = targetKey.slice(i + 1);
  if (!handle || platform === "local" || platform === "import") return null;
  if (platform === "linkedin") return `https://www.linkedin.com/in/${handle}`;
  if (platform === "x") return `https://x.com/${handle}`;
  if (platform === "github") return `https://github.com/${handle}`;
  return `https://${platform}/${handle}`;
}
